import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Radar, ArrowLeft, Check, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  // If we have a token, show the reset form. Otherwise show the request form.
  return token ? <ResetForm token={token} navigate={navigate} /> : <RequestForm />;
}

function RequestForm() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetUrl, setResetUrl] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      // In dev mode, we get the reset URL directly
      if (res.data.reset_url) {
        setResetUrl(res.data.reset_url);
      }
      toast.success("Reset link generated");
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("Too many requests. Please wait a moment.");
      } else {
        toast.error(err.response?.data?.detail || "Request failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--n-bg)] px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[var(--hig-accent)] mb-4">
            <Radar className="w-7 h-7 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Reset Password</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Enter your email to receive a reset link
          </p>
        </div>

        <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
          <CardContent className="pt-6">
            {sent ? (
              <div className="space-y-4 text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
                  <Check className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <p className="text-sm text-[var(--n-text-secondary)]">
                  If an account exists with that email, a reset link has been generated.
                </p>
                {resetUrl && (
                  <div className="glass-card rounded-lg p-3 text-left">
                    <p className="text-[10px] text-muted-foreground mb-1">Dev mode — reset link:</p>
                    <Link
                      to={resetUrl}
                      className="text-xs text-[var(--hig-accent)] hover:text-[var(--hig-accent)] break-all"
                      data-testid="reset-link"
                    >
                      {window.location.origin}{resetUrl}
                    </Link>
                  </div>
                )}
                <Link
                  to="/login"
                  className="text-sm text-[var(--hig-accent)] hover:text-[var(--hig-accent)] inline-flex items-center gap-1"
                >
                  <ArrowLeft className="w-3 h-3" /> Back to login
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm text-muted-foreground">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    data-testid="reset-email-input"
                    className="bg-[var(--n-bg)] border-[var(--n-border-2)] focus:border-[var(--hig-accent)]"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-submit-btn"
                  className="w-full bg-[var(--hig-accent)] hover:opacity-90 text-primary-foreground font-semibold"
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
                <p className="text-center">
                  <Link to="/login" className="text-sm text-[var(--hig-accent)] hover:text-[var(--hig-accent)] inline-flex items-center gap-1">
                    <ArrowLeft className="w-3 h-3" /> Back to login
                  </Link>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ResetForm({ token, navigate }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      toast.success("Password reset successfully! Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--n-bg)] px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[var(--hig-accent)] mb-4">
            <Radar className="w-7 h-7 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Set New Password</h1>
          <p className="text-muted-foreground mt-1 text-sm">Choose a strong password</p>
        </div>

        <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">New Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 8 characters"
                    required
                    data-testid="new-password-input"
                    className="bg-[var(--n-bg)] border-[var(--n-border-2)] focus:border-[var(--hig-accent)] pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Confirm Password</Label>
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter password"
                  required
                  data-testid="confirm-password-input"
                  className="bg-[var(--n-bg)] border-[var(--n-border-2)] focus:border-[var(--hig-accent)]"
                />
              </div>
              {password && password.length < 8 && (
                <p className="text-xs text-red-600 dark:text-red-400">Password must be at least 8 characters</p>
              )}
              {confirm && password !== confirm && (
                <p className="text-xs text-red-600 dark:text-red-400">Passwords don't match</p>
              )}
              <Button
                type="submit"
                disabled={loading || password.length < 8 || password !== confirm}
                data-testid="set-password-btn"
                className="w-full bg-[var(--hig-accent)] hover:opacity-90 text-primary-foreground font-semibold"
              >
                {loading ? "Resetting..." : "Set New Password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
